import { startCase } from "lodash";

import type {
  Account,
  AccountType,
  Margin,
  PaperAccount,
} from "../api/rest/account";

export interface AccountLabels {
  title: string;
  subTitle: string;
  collapsedSubTitle: string;
}

type LiveAccountTitleMapKey = Extract<
  AccountType,
  "ira" | "legal_entity" | "trading"
>;

export const LIVE_ACCOUNT_TITLE_MAP: Record<LiveAccountTitleMapKey, string> = {
  ira: "Individual Retirement",
  legal_entity: "Business Trading",
  trading: "Individual Trading",
};

export const getLiveAccountLabels = ({
  account,
  margin,
}: {
  account: Account | undefined;
  margin: Margin | undefined;
}): AccountLabels => {
  const number = margin?.account_number ?? account?.account_number ?? "";
  const isIRA = account?.account_type === "ira";
  const iraSubType = isIRA ? startCase(account?.account_sub_type ?? "") : "";
  const isLiveAccountApplication =
    account?.status === "ONBOARDING" || account?.status === "PAPER_ONLY";

  const title = account?.account_name
    ? account?.account_name
    : isLiveAccountApplication
      ? "Live Account Application"
      : (LIVE_ACCOUNT_TITLE_MAP[
          account?.account_type as LiveAccountTitleMapKey
        ] ?? LIVE_ACCOUNT_TITLE_MAP.trading);

  const subTitle = isIRA
    ? `${iraSubType} IRA ${number}`
    : `Brokerage Account ${number}`;

  const collapsedSubTitle = isIRA
    ? `${iraSubType} IRA - ${number}`
    : `Brokerage - ${number}`;

  return { title, subTitle, collapsedSubTitle };
};

export const getPaperAccountLabels = ({
  account,
  margin,
}: {
  account: PaperAccount | undefined;
  margin: Margin | undefined;
}): AccountLabels => {
  const title = account?.name || "Paper Trading";
  const number = margin?.account_number ?? "";

  return {
    title,
    subTitle: `Paper Account ${number}`,
    collapsedSubTitle: `Paper - ${number}`,
  };
};
